import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CountUp from 'react-countup';
import { useScale } from '../Scale';

import PlayAgainImg from '../../assets/img/play-again.png';
import HomeImg from '../../assets/img/home.png';
import './GameResults.scss';
import { DisconnectTypes } from '../../game/Types';
import { calculateGemsXP, playVideoAd } from '../../helpers';
import { crazygamesSDK } from '../../crazygames/sdk';
import { updatePB, getEncouragingMessage, formatTime } from '../../game/PersonalBest';

// Smarter video ad logic to prevent spammed ads
const DEATHS_BETWEEN_ADS = 1;
const MIN_TIME_BETWEEN_ADS_MS = 1000 * 60 * 1;

function shouldShowVideoAd(): boolean {
  const windowAny = window as any;
  const adProvider = windowAny?.adProvider || 'adinplay';

  // Only show video ads for CrazyGames
  if (adProvider !== 'crazygames') {
    return false;
  }

  try {
    // Get death count
    const deathCount = parseInt(localStorage.getItem('deathCountForAds') || '0');
    const newDeathCount = deathCount + 1;
    localStorage.setItem('deathCountForAds', newDeathCount.toString());

    // Get last ad time
    const lastAdTime = parseInt(localStorage.getItem('lastDeathAdTime') || '0');
    const timeSinceLastAd = Date.now() - lastAdTime;

    // Show ad if applicable
    if (newDeathCount >= DEATHS_BETWEEN_ADS && timeSinceLastAd > MIN_TIME_BETWEEN_ADS_MS) {
      // Reset counter and update last ad time
      localStorage.setItem('deathCountForAds', '0');
      localStorage.setItem('lastDeathAdTime', Date.now().toString());
      return true;
    }

    return false;
  } catch (e) {
    console.error('Error in shouldShowVideoAd:', e);
    return false;
  }
}

function GameResults({ onHome, results, game, isLoggedIn, adElement }: any) {
  const xpBonusExpiry = useSelector((state: any) => state.account?.dailyLogin?.xpBonus);
  const xpBonusActive = xpBonusExpiry && xpBonusExpiry > Date.now();

  const pbResult = useMemo(() => updatePB({
    coins: results.coins || 0,
    kills: results.kills || 0,
    survivalTime: results.survivalTime || 0,
  }), [results]);

  useEffect(() => {
    if (shouldShowVideoAd()) {
      console.log('[GameResults] Showing video ad after death');
      playVideoAd().then(() => {
        console.log('[GameResults] Video ad completed or skipped');
      });
    } else {
      console.log('[GameResults] Skipping video ad this time');
    }
  }, []);

  // Trigger happy time for good games
  useEffect(() => {
    try {
      const coins = results?.coins || 0;
      const kills = results?.kills || 0;
      const survivalTime = results?.survivalTime || 0;

      if (coins >= 1000000) {
        console.log('[CrazyGames] Happy time! 1M+ coins achieved!');
        crazygamesSDK.happytime();
      } else if (kills >= 200) {
        console.log('[CrazyGames] Happy time! 200+ kills achieved!');
        crazygamesSDK.happytime();
            } else if (survivalTime >= 3600) { // 1 hour
        console.log('[CrazyGames] Happy time! Survived 1+ hours!');
        crazygamesSDK.happytime();
      }
    } catch (error) {
      console.error('[CrazyGames] Error triggering happy time:', error);
    }
  }, [results]);

  useEffect(() => {
    const timer = setTimeout(() => {
      game.events.emit('goHome');
      onHome();
      game.events.emit('setGameResults', null);
      game.events.emit('startSpectate');
    }, 150000);
    return () => clearTimeout(timer);
  }, []);

  const calculateDropAmount = (coins: number) => {
    return coins < 13 ? 10 : Math.round(coins < 25000 ? coins * 0.8 : Math.log10(coins) * 30000 - 111938.2002602);
  };
  const hasEnoughCoins = results.coins >= 10000;
  const hasEnoughTime = results.survivalTime >= 120;
  const insuranceCoins = results.insuranceRespawnCoins || 0;
  const normalRespawnCoins = hasEnoughCoins && hasEnoughTime ? Math.round(calculateDropAmount(results.coins) / 2) : 0;
  const respawnCoins = insuranceCoins > 0 ? insuranceCoins : normalRespawnCoins;
  const coinProgress = Math.min(results.coins / 10000, 1);
  const timeProgress = Math.min(results.survivalTime / 120, 1);

  const onHomeClick = () => {
    if (respawnCoins > 0) {
      game.events.emit('pendingRespawnInfo', { coins: respawnCoins, expiresAt: Date.now() + 120000 });
    }
    game.events.emit('goHome');
    onHome();
    game.events.emit('setGameResults', null);
    game.events.emit('startSpectate');
  };
  const onRestartClick = () => {
    // playVideoAd().then(() => {
    // game.events.emit('setGameResults', null);
    // game.events.emit('restartGame');
    // });

    function go() {
    game.events.emit('setGameResults', null);
    game.events.emit('restartGame');
    }
    go();

    // if((window as any).adBreak) {
    //   console.log('adBreak');
    //   (window as any).adBreak({
    //     type: 'next',
    //     adBreakDone: (e: any) => {
    //       console.log('adBreakDone', e);
    //     },  // always called, unblocks the game logic
    //   });
    // }
  };

  return (
    <div className="results" style={useScale(true).styles}>
      <div className='results-main'>
      <div className="results-title">
        {results.disconnectReason?.code === DisconnectTypes.Player ? 'You got stabbed' : results.disconnectReason?.code === DisconnectTypes.Mob ? 'You were destroyed' : 'You were disconnected'}
        <br />
      </div>

      <div className="results-container">
        <div className="info">
          <div className="title">{results.disconnectReason?.code === DisconnectTypes.Player ? 'Stabbed by' : results.disconnectReason?.code === DisconnectTypes.Mob ? 'By' : 'Disconnect reason:'}</div>
          {results.disconnectReason?.reason}
        </div>

        <div className="info">
          <div className="title">Coins:</div>
          <CountUp
            duration={3}
            end={results.coins}
          />
        </div>

        <div className="info">
          <div className="title">Stabs:</div>
          <CountUp
            duration={3}
            end={results.kills}
          />
        </div>

        <div className="info">
          <div className="title">Survived:</div>
          <CountUp
            end={results.survivalTime}
            duration={3}
            formattingFn={(s) => `${Math.floor((s % 3600) / 60)}m ${Math.floor(s % 60)}s`}
          />
        </div>
        { isLoggedIn && (
          <>
        <div className="info">
          <div className="title">Gems Gained</div>
          <CountUp
            end={calculateGemsXP(results.coins, results.kills, 0).gems}
            duration={3}
          />
        </div>
        <div className="info">
          <div className="title" style={xpBonusActive ? { color: '#ffeb3b' } : undefined}>XP Gained{xpBonusActive ? ' (2x)' : ''}</div>
          <CountUp
            end={calculateGemsXP(results.coins, results.kills, 0).xp * (xpBonusActive ? 2 : 1)}
            duration={3}
          />
        </div>
        <div className="info">
          <div className="title">Mastery Earned</div>
          <CountUp
            end={calculateGemsXP(results.coins, results.kills, 0).mastery}
            duration={3}
          />
        </div>
        </>
        )}
      </div>

      {/* <div className="personal-best-section">
        {pbResult.anyRecord && <div className="pb-header new-record">NEW RECORD!</div>}
        {!pbResult.anyRecord && <div className="pb-header">Personal Best</div>}
        <div className="pb-stats">
          <div className={`pb-stat${pbResult.records.coins ? ' is-record' : ''}`}>
            <span className="pb-label">Coins</span>
            <span className="pb-value">{pbResult.pb.coins.toLocaleString()}</span>
            {pbResult.records.coins && <span className="pb-badge">NEW!</span>}
            {!pbResult.records.coins && <span className="pb-encourage">{getEncouragingMessage(results.coins, pbResult.pb.coins)}</span>}
          </div>
          <div className={`pb-stat${pbResult.records.kills ? ' is-record' : ''}`}>
            <span className="pb-label">Stabs</span>
            <span className="pb-value">{pbResult.pb.kills.toLocaleString()}</span>
            {pbResult.records.kills && <span className="pb-badge">NEW!</span>}
            {!pbResult.records.kills && <span className="pb-encourage">{getEncouragingMessage(results.kills, pbResult.pb.kills)}</span>}
          </div>
          <div className={`pb-stat${pbResult.records.survivalTime ? ' is-record' : ''}`}>
            <span className="pb-label">Survived</span>
            <span className="pb-value">{formatTime(pbResult.pb.survivalTime)}</span>
            {pbResult.records.survivalTime && <span className="pb-badge">NEW!</span>}
            {!pbResult.records.survivalTime && <span className="pb-encourage">{getEncouragingMessage(results.survivalTime, pbResult.pb.survivalTime)}</span>}
          </div>
        </div>
      </div> */}

      <div className="results-buttons">
        {respawnCoins > 0 ? (
          <div className="respawn-info respawn-available">
            <span className="respawn-icon">&#x1F4B0;</span>
            {insuranceCoins > 0 ? (
              <span style={{color: '#ff00f2', fontWeight: 'bold'}}>Insurance activated! Respawn with {insuranceCoins.toLocaleString()} coins!</span>
            ) : (
              <span>Press Play Again to respawn with <strong>{respawnCoins.toLocaleString()}</strong> coins!</span>
            )}
          </div>
        ) : (
          <div className="respawn-info">
            <div className="respawn-progress-label">Respawn Progress</div>
            <div className="respawn-progress-bars">
              <div className="respawn-bar-row">
                <span>Coins: {results.coins.toLocaleString()} / 10,000</span>
                <div className="respawn-bar"><div className="respawn-bar-fill" style={{ width: `${Math.min(coinProgress * 100, 100)}%` }} /></div>
              </div>
              <div className="respawn-bar-row">
                <span>Time: {Math.floor(results.survivalTime / 60)}:{String(Math.floor(results.survivalTime % 60)).padStart(2, '0')} / 2:00</span>
                <div className="respawn-bar"><div className="respawn-bar-fill" style={{ width: `${Math.min(timeProgress * 100, 100)}%` }} /></div>
              </div>
            </div>
            <div className="respawn-hint">Reach both to keep coins on respawn!</div>
          </div>
        )}
        { results.disconnectReason?.type !== DisconnectTypes.Server && (
        <div
          className={`play-again ${(() => { try { return JSON.parse(sessionStorage.getItem('swordbattle:tutorialSession') || '{}').active ? 'tutorial-pulse' : ''; } catch { return ''; } })()}`}
          role="button"
          onClick={onRestartClick}
          onKeyDown={event => event.key === 'Enter' && onRestartClick()}
          tabIndex={0}
        >
          <img src={PlayAgainImg} alt="Play again" />
        </div>
        )}
        <div
          className="to-home"
          role="button"
          onClick={onHomeClick}
          onKeyDown={event => event.key === 'Enter' && onHomeClick()}
          tabIndex={0}
        >
          <img src={HomeImg} alt="Home" />
        </div>
</div>

      </div>
      { adElement ? (
          <div className="ad">
            {adElement}
          </div>
        ) : null}
    </div>
  )
}

export default GameResults;
